import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChefHat, Rocket, Globe, Shield, CheckCircle2, ArrowRight, Layout, Palette, Image as ImageIcon, Sparkles, Star, Zap, Building2, MousePointer2, Globe2, Code2 } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { usePreventBack } from '../hooks/usePreventBack';
import LocationSearch from '../components/LocationSearch';

export default function Portal() {
    const navigate = useNavigate();
    
    const handleDelayNavigate = (path) => {
        setTimeout(() => {
            navigate(path);
        }, 150);
    };
    // Dynamic Tidio Chatbot Integration
    useEffect(() => {
        const script = document.createElement('script');
        script.src = "//code.tidio.co/ufb6bdlk5pytal8uujtcbq2yasrqwmde.js";
        script.async = true;
        document.body.appendChild(script);

        return () => {
            // Clean up chatbot widget on component unmount
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
            const tidioWidget = document.getElementById('tidio-chat');
            if (tidioWidget) {
                tidioWidget.remove();
            }
        };
    }, []);

    const [step, setStep] = useState('landing'); // 'landing', 'register', 'plans', 'checkout', 'choice', 'template-config', 'custom-design', 'success'
    const [preselectedPlan, setPreselectedPlan] = useState(false);
    const [pricingTab, setPricingTab] = useState('restaurant'); // 'restaurant' or 'builder'
    usePreventBack(step !== 'landing');
    const [addressMode, setAddressMode] = useState('map'); // 'map' or 'manual'
    const [formData, setFormData] = useState({
        name: '', email: '', password: '', phone: '', slug: '', address: '', city: 'Chennai',
        latitude: 13.0827, longitude: 80.2707
    });
    const [selectedPlan, setSelectedPlan] = useState({
        name: 'Professional',
        billingCycle: 'monthly',
        price: 6999
    });
    const [cardData, setCardData] = useState({
        cardNumber: '',
        cardName: '',
        cardExpiry: '',
        cardCvv: ''
    });
    const [configData, setConfigData] = useState({
        primaryColor: '#5b4310',
        secondaryColor: '#000000',
        logoUrl: '',
        notes: '',
        planName: 'Basic'
    });
    const [loading, setLoading] = useState(false);
    const [regResponse, setRegResponse] = useState(null);
    const [showTestCredentials, setShowTestCredentials] = useState(false);
    const [activePolicy, setActivePolicy] = useState(null); // null, 'privacy', 'terms', 'refund'
    const toast = useToast();
    const [uploading, setUploading] = useState(false);
    const [checkingSlug, setCheckingSlug] = useState(false);
    const [slugStatus, setSlugStatus] = useState(null); // null, 'checking', 'available', 'taken'

    useEffect(() => {
        if (!formData.slug) {
            setSlugStatus(null);
            return;
        }
        setSlugStatus('checking');
        const delayDebounceFn = setTimeout(async () => {
            try {
                const res = await api.get(`/api/restaurants/slug/${formData.slug}`).catch(err => err.response);
                if (res && res.status === 200) {
                    setSlugStatus('taken');
                } else {
                    setSlugStatus('available');
                }
            } catch (err) {
                setSlugStatus('available');
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [formData.slug]);

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size exceeds the 2 MB limit');
            return;
        }

        setUploading(true);
        try {
            const uploadData = new FormData();
            uploadData.append('image', file);
            uploadData.append('type', 'logo');
            if (formData.name) {
                uploadData.append('restaurantName', formData.name);
            }

            const response = await api.post('/api/upload', uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data && response.data.secureUrl) {
                setConfigData(prev => ({ ...prev, logoUrl: response.data.secureUrl }));
                toast.success('Logo uploaded successfully');
            } else {
                toast.error('Failed to upload logo');
            }
        } catch (err) {
            console.error('Logo upload error:', err);
            toast.error(err.response?.data?.message || 'Failed to upload logo');
        } finally {
            setUploading(false);
        }
    };

    // Helper to deduce card brand
    const getCardBrand = (number) => {
        const cleaned = number.replace(/\D/g, '');
        if (cleaned.startsWith('4')) return 'Visa';
        if (cleaned.startsWith('5')) return 'Mastercard';
        if (cleaned.startsWith('3')) return 'American Express';
        if (cleaned.startsWith('6')) return 'Discover';
        return 'Card';
    };

    const handleRegisterStepSubmit = async (e) => {
        e.preventDefault();
        if (!formData.slug) {
            toast.error('Subdomain Link is required');
            return;
        }
        if (slugStatus === 'taken') {
            toast.error('This subdomain link is already registered. Please choose a different link.');
            return;
        }
        if (slugStatus === 'checking') {
            toast.warning('Checking subdomain availability... Please wait.');
            return;
        }
        setStep(preselectedPlan ? 'checkout' : 'plans');
    };

    const handleRegisterSubscription = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...formData,
                planName: selectedPlan.name,
                billingCycle: selectedPlan.billingCycle
            };

            const initRes = await api.post('/api/restaurants/initiate-subscription', payload);

            if (initRes.data.provider === 'razorpay') {
                const subscriptionId = initRes.data.subscriptionId;
                const keyId = initRes.data.keyId;

                const rzpScript = document.createElement('script');
                rzpScript.src = 'https://checkout.razorpay.com/v1/checkout.js';
                rzpScript.async = true;
                document.body.appendChild(rzpScript);

                rzpScript.onload = () => {
                    const options = {
                        key: keyId,
                        subscription_id: subscriptionId,
                        name: 'Aaharam Cloud',
                        description: `30-Day Free Trial - ${selectedPlan.name} Plan`,
                        config: {
                            display: {
                                blocks: {
                                    cardBlock: {
                                        name: 'Pay using Card',
                                        instruments: [{ method: 'card' }]
                                    }
                                },
                                sequence: ['block.cardBlock'],
                                preferences: {
                                    show_default_blocks: false
                                }
                            }
                        },
                        handler: async function (rzpRes) {
                            try {
                                setLoading(true);
                                const compRes = await api.post('/api/restaurants/complete-registration', {
                                    razorpay_payment_id: rzpRes.razorpay_payment_id,
                                    razorpay_subscription_id: rzpRes.razorpay_subscription_id,
                                    razorpay_signature: rzpRes.razorpay_signature,
                                    registrationData: initRes.data.registrationData
                                });
                                setRegResponse(compRes.data);
                                if (compRes.data.token) {
                                    localStorage.setItem('token', compRes.data.token);
                                }
                                if (compRes.data.testUsers) {
                                    setShowTestCredentials(true);
                                }
                                toast.success('Subscription authorized & Restaurant account activated!');
                                setStep('choice');
                            } catch (cErr) {
                                console.error('Complete registration error', cErr);
                                toast.error(cErr.response?.data?.message || 'Failed to complete account activation');
                            } finally {
                                setLoading(false);
                            }
                        },
                        prefill: {
                            name: formData.name,
                            email: formData.email,
                            contact: formData.phone
                        },
                        modal: {
                            ondismiss: function () {
                                setLoading(false);
                            }
                        }
                    };
                    const rzp = new window.Razorpay(options);
                    rzp.open();
                };
            } else if (initRes.data.subscriptionAuthUrl) {
                toast.success('Initiated! Opening payment gateway to authorize your trial...');
                window.location.href = initRes.data.subscriptionAuthUrl;
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Registration initiation failed');
            setLoading(false);
        }
    };


    const handleTemplateSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.put('/api/restaurants/config', {
                id: regResponse.restaurant.id,
                ...configData
            });
            toast.success('Design updated!');
            setStep('success');
        } catch (err) {
            toast.error('Failed to update design');
        } finally {
            setLoading(false);
        }
    };

    const handleCustomDesignSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/api/restaurants/design-request', {
                restaurantId: regResponse.restaurant.id,
                email: formData.email,
                saasPlan: selectedPlan.name,
                referenceUrls: configData.referenceUrls || '',
                businessDescription: configData.notes,
                ...configData
            });
            toast.success('Design request submitted!');
            setStep('success-custom');
        } catch (err) {
            toast.error('Failed to submit request');
        } finally {
            setLoading(false);
        }
    };

    // Shared Components
    const Navbar = () => (
        <nav className="bg-white border-b px-3 sm:px-6 lg:px-8 py-3.5 sm:py-4 flex items-center justify-between sticky top-0 z-50">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setStep('landing')}>
                <img src="/aaharam_logo.jpg" alt="Aaharam Cloud" className="w-14 h-14 rounded-xl object-contain" />
                <span className="text-lg sm:text-2xl font-black text-gray-900 tracking-tighter">
                    AAHARAM <span className="text-primary-600">CLOUD</span>
                </span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
                <Link to="/login" className="text-gray-600 hover:text-gray-900 font-black uppercase tracking-widest text-[10px] sm:text-xs px-2 sm:px-4">Login</Link>
                <button onClick={() => setStep('register')} className="btn-primary py-2 px-3 sm:px-6 shadow-lg shadow-primary-200 font-black uppercase tracking-widest text-[10px] sm:text-xs whitespace-nowrap">
                    <span className="sm:inline hidden">Launch Your Restaurant</span>
                    <span className="sm:hidden inline">Launch Restaurant</span>
                </button>
            </div>
        </nav>
    );

    const renderTestCredentialsModal = () => {
        if (!regResponse || !regResponse.testUsers) return null;
        return (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-y-auto">
                <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-100 flex flex-col max-h-[90vh]">
                    <div className="bg-gradient-to-r from-primary-600 to-indigo-600 p-6 text-white text-center relative flex-shrink-0">
                        <Sparkles className="w-12 h-12 mx-auto mb-2 text-amber-300 animate-pulse" />
                        <h3 className="text-2xl font-black">Demo Test Accounts Created!</h3>
                        <p className="text-slate-100/90 text-sm mt-1">Use these credentials to test your restaurant's roles.</p>
                    </div>
                    <div className="p-6 space-y-4 overflow-y-auto flex-1">
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-amber-800 text-xs font-medium">
                            <span className="font-bold">Note:</span> Use the password <span className="font-bold underline">123456</span> for Customer, Kitchen, and Delivery roles, and your registered password for the Restaurant Admin.
                        </div>
                        <div className="space-y-3">
                            {/* Restaurant Admin */}
                            {regResponse.user && (
                                <div className="flex justify-between items-center bg-indigo-50/50 p-4 rounded-2xl border border-indigo-100 hover:border-indigo-200 transition-colors">
                                    <div className="text-left">
                                        <p className="text-[10px] uppercase font-black text-indigo-500 tracking-wider">Restaurant Admin (You)</p>
                                        <p className="text-slate-800 font-mono text-sm font-bold select-all">{regResponse.user.email}</p>
                                        <p className="text-[10px] text-slate-500 mt-0.5">Password: <span className="font-bold">{formData.password}</span></p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(regResponse.user.email);
                                            toast.success('Admin email copied!');
                                        }}
                                        className="px-3 py-1.5 bg-white text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl text-xs font-bold border border-indigo-200 transition-all"
                                    >
                                        Copy
                                    </button>
                                </div>
                            )}

                            {/* Default Roles */}
                            {regResponse.testUsers.map((u) => {
                                const roleLabel = {
                                    customer: 'Customer',
                                    kitchen_staff: 'Kitchen Staff',
                                    delivery_partner: 'Delivery Partner'
                                }[u.role] || u.role;
                                return (
                                    <div key={u.id} className="flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-slate-100 hover:border-primary-100 transition-colors">
                                        <div className="text-left">
                                            <p className="text-[10px] uppercase font-black text-slate-400 tracking-wider">{roleLabel}</p>
                                            <p className="text-slate-800 font-mono text-sm font-bold select-all">{u.email}</p>
                                            <p className="text-[10px] text-slate-500 mt-0.5">Password: <span className="font-bold">123456</span></p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard.writeText(u.email);
                                                toast.success(`${roleLabel} email copied!`);
                                            }}
                                            className="px-3 py-1.5 bg-white text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl text-xs font-bold border border-slate-200 transition-all"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowTestCredentials(false)}
                            className="w-full btn-primary py-4 text-sm font-black uppercase tracking-widest shadow-lg shadow-primary-200 mt-4"
                        >
                            Got it, Start Testing
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderPolicyModal = () => {
        if (!activePolicy) return null;

        const content = {
            privacy: {
                title: 'Privacy Policy',
                body: (
                    <div className="space-y-4 text-xs md:text-sm text-slate-600 leading-relaxed text-left">
                        <p className="font-bold text-slate-800">Last Updated: June 2026</p>
                        <p>Welcome to Aaharam Cloud ("we", "our", or "us"). We are committed to protecting your personal data and privacy under the Information Technology Act, 2000, and the SPDI (Sensitive Personal Data or Information) Rules, 2011.</p>
                        <h4 className="font-bold text-slate-800">1. Information We Collect</h4>
                        <p>We collect restaurant details, admin contact information, payment credentials, billing details, and transactional data generated via our SaaS portals to maintain and optimize your cloud kitchen software.</p>
                        <h4 className="font-bold text-slate-800">2. Sharing & Security</h4>
                        <p>We do not sell your personal data. We only share information with third-party service providers (like Cashfree for payments, or AWS for hosting) to fulfill platform services. We employ industry-standard encryption protocols (SSL/TLS) to secure database records.</p>
                        <h4 className="font-bold text-slate-800">3. Cookies</h4>
                        <p>We use session cookies to keep platform administrators and customer accounts authenticated across subdomain sessions.</p>
                    </div>
                )
            },
            terms: {
                title: 'Terms of Service',
                body: (
                    <div className="space-y-4 text-xs md:text-sm text-slate-600 leading-relaxed text-left">
                        <p className="font-bold text-slate-800">Last Updated: June 2026</p>
                        <p>By registering a restaurant or accessing Aaharam Cloud websites and dashboards, you agree to be bound by these Terms of Service.</p>
                        <h4 className="font-bold text-slate-800">1. SaaS Subscription Agreement</h4>
                        <p>We grant you a limited, non-exclusive, non-transferable license to access our online ordering templates, admin consoles, KDS display portals, and delivery coordination dashboards according to your subscribed tier (Starter, Professional, or Premium).</p>
                        <h4 className="font-bold text-slate-800">2. Usage Rules</h4>
                        <p>You agree not to reverse engineer the portal code, perform automated scraping on cloud kitchen nodes, or use the customer ordering workflows for illegal trade or fraudulent transactions.</p>
                        <h4 className="font-bold text-slate-800">3. Liability Limits</h4>
                        <p>Aaharam Cloud will not be held liable for temporary service interruptions, delivery logistics issues, or hardware display screen faults in your physical kitchens.</p>
                    </div>
                )
            },
            refund: {
                title: 'Refund & Cancellation Policy',
                body: (
                    <div className="space-y-4 text-xs md:text-sm text-slate-600 leading-relaxed text-left">
                        <p className="font-bold text-slate-800">Last Updated: June 2026</p>
                        <h4 className="font-bold text-slate-800">1. Subscription Cancellations</h4>
                        <p>You can cancel your subscription at any time directly through the restaurant admin panel or the Cashfree customer billing center. Upon cancellation, your access remains active until the end of the current billing cycle.</p>
                        <h4 className="font-bold text-slate-800">2. 30-Day Free Trial</h4>
                        <p>Every newly registered restaurant is eligible for a 30-day free trial. Your card will not be charged during this trial period. If you cancel before the trial ends, no charge will be made.</p>
                        <h4 className="font-bold text-slate-800">3. Refund Processing</h4>
                        <p>Due to the nature of SaaS services, subscription fees are non-refundable once billed. If you believe there was a billing error or double-charge, contact support at billing@aaharamcloud.in. Approved refunds will be credited back via Cashfree within 5-7 business days.</p>
                    </div>
                )
            }
        }[activePolicy];

        return (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-100 flex flex-col max-h-[85vh]">
                    <div className="bg-slate-950 p-6 text-white text-center relative flex-shrink-0">
                        <h3 className="text-xl font-black uppercase tracking-wider">{content.title}</h3>
                    </div>
                    <div className="p-6 overflow-y-auto flex-1">
                        {content.body}
                    </div>
                    <div className="p-6 border-t border-slate-100 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setActivePolicy(null)}
                            className="w-full bg-slate-900 text-white font-black uppercase tracking-widest text-xs py-4 rounded-xl shadow-lg hover:bg-slate-800 transition-all"
                        >
                            Close Legal Page
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (step === 'landing') {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navbar />
                {/* Hero */}
                <div className="relative overflow-hidden bg-white pt-6 pb-24 lg:pt-10 lg:pb-40">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <div className="inline-flex items-center space-x-2 bg-primary-50 text-primary-700 px-4 py-1.5 rounded-full text-sm font-bold mb-8">
                            <Sparkles className="w-4 h-4" />
                            <span>Voted #1 Platform for Cloud Kitchens</span>
                        </div>
                        <h1 className="text-4xl sm:text-6xl lg:text-8xl font-black text-gray-900 leading-none tracking-tight">
                            Build Your <span className="text-primary-600">Food</span> Business.
                        </h1>
                        <p className="mt-8 text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
                            Launch your restaurant, build your franchise, or create a stunning website — all from one platform. Zero commissions, zero hassle.
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row justify-center items-center gap-4 px-4 sm:px-0">
                            <button onClick={() => { setPreselectedPlan(false); setStep('register'); }} className="btn-primary py-5 sm:py-6 px-8 sm:px-12 text-lg sm:text-xl shadow-2xl shadow-primary-300 w-full sm:w-auto transform active:scale-95 transition-all duration-200">
                                Launch Your Restaurant
                            </button>
                            <button
                                onClick={() => handleDelayNavigate('/franchise-onboard')}
                                className="btn-secondary py-5 sm:py-6 px-8 sm:px-12 text-lg sm:text-xl flex items-center justify-center w-full sm:w-auto bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-300/40 transform active:scale-95 transition-all duration-200"
                            >
                                <Sparkles className="mr-2 w-5 h-5 text-indigo-600 animate-pulse" /> Build Your Franchise
                            </button>
                            <button
                                onClick={() => handleDelayNavigate('/sites/login')}
                                className="btn-secondary py-5 sm:py-6 px-8 sm:px-12 text-lg sm:text-xl flex items-center justify-center w-full sm:w-auto bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200/60 transform active:scale-95 transition-all duration-200"
                            >
                                <Globe2 className="mr-2 w-5 h-5 text-emerald-600" /> Build Your Website
                            </button>
                        </div>
                    </div>
                </div>

                {/* Features */}
                <div className="py-16 sm:py-24 bg-gray-900 text-white overflow-hidden relative">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 font-black text-center">
                            <div><p className="text-3xl sm:text-5xl text-primary-500">100+</p><p className="uppercase text-gray-500 text-[10px] sm:text-xs tracking-widest mt-2">Active Tenants</p></div>
                            <div><p className="text-3xl sm:text-5xl text-primary-500">2M+</p><p className="uppercase text-gray-500 text-[10px] sm:text-xs tracking-widest mt-2">Orders Processed</p></div>
                            <div><p className="text-3xl sm:text-5xl text-primary-500">99.9%</p><p className="uppercase text-gray-500 text-[10px] sm:text-xs tracking-widest mt-2">Uptime Guarantee</p></div>
                            <div><p className="text-3xl sm:text-5xl text-primary-500">₹0</p><p className="uppercase text-gray-500 text-[10px] sm:text-xs tracking-widest mt-2">Commission Fees</p></div>
                        </div>
                    </div>
                </div>

                {/* Our Products Section */}
                <div className="py-20 sm:py-28 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl lg:text-6xl font-black text-gray-900 leading-none tracking-tight">
                                Three Products. <span className="text-primary-600">One Platform.</span>
                            </h2>
                            <p className="mt-4 text-lg text-gray-500 font-medium max-w-xl mx-auto">
                                Everything you need to launch, scale, and grow your food business online.
                            </p>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                            {/* Launch Your Restaurant */}
                            <div className="group bg-white rounded-3xl p-8 border-2 border-slate-100 hover:border-primary-500 hover:shadow-2xl transform active:scale-95 transition-all duration-200 cursor-pointer" onClick={() => { setPreselectedPlan(false); setStep('register'); }}>
                                <div className="w-16 h-16 bg-primary-50 group-hover:bg-primary-600 rounded-2xl flex items-center justify-center mb-6 transition-colors">
                                    <ChefHat className="w-8 h-8 text-primary-600 group-hover:text-white transition-colors" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-3">Launch Your Restaurant</h3>
                                <p className="text-gray-500 text-sm leading-relaxed mb-6">Get your own ordering website, admin dashboard, kitchen display, delivery tracking, and mobile apps — all on your branded subdomain.</p>
                                <div className="text-primary-600 font-black text-sm flex items-center group-hover:gap-2 transition-all">
                                    Get Started <ArrowRight className="w-4 h-4 ml-1" />
                                </div>
                            </div>

                            {/* Build Your Franchise */}
                            <div onClick={() => handleDelayNavigate('/franchise-onboard')} className="group bg-white rounded-3xl p-8 border-2 border-slate-100 hover:border-indigo-500 hover:shadow-2xl transform active:scale-95 transition-all duration-200 cursor-pointer">
                                <div className="w-16 h-16 bg-indigo-50 group-hover:bg-indigo-600 rounded-2xl flex items-center justify-center mb-6 transition-colors">
                                    <Building2 className="w-8 h-8 text-indigo-600 group-hover:text-white transition-colors" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-3">Build Your Franchise</h3>
                                <p className="text-gray-500 text-sm leading-relaxed mb-6">Expand your brand with multi-branch management, centralized menus, royalty tracking, and unified customer experience across all outlets.</p>
                                <div className="text-indigo-600 font-black text-sm flex items-center group-hover:gap-2 transition-all">
                                    Register Franchise <ArrowRight className="w-4 h-4 ml-1" />
                                </div>
                            </div>

                            {/* Build Your Website */}
                            <div onClick={() => handleDelayNavigate('/sites/login')} className="group bg-white rounded-3xl p-8 border-2 border-slate-100 hover:border-emerald-500 hover:shadow-2xl transform active:scale-95 transition-all duration-200 relative overflow-hidden cursor-pointer">
                                <span className="absolute top-4 right-4 bg-emerald-500 text-white text-[9px] font-black uppercase px-3 py-1 rounded-full tracking-wider">New</span>
                                <div className="w-16 h-16 bg-emerald-50 group-hover:bg-emerald-600 rounded-2xl flex items-center justify-center mb-6 transition-colors">
                                    <Globe2 className="w-8 h-8 text-emerald-600 group-hover:text-white transition-colors" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-3">Build Your Website</h3>
                                <p className="text-gray-500 text-sm leading-relaxed mb-6">Create stunning restaurant landing pages, catering portfolios, and café websites with our drag-and-drop builder. No coding required.</p>
                                <div className="text-emerald-600 font-black text-sm flex items-center group-hover:gap-2 transition-all">
                                    Start Building <ArrowRight className="w-4 h-4 ml-1" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pricing Plans Section */}
                <div className="py-24 bg-white text-left">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-10">
                            <h2 className="text-4xl lg:text-6xl font-black text-gray-900 leading-none tracking-tight">
                                Simple, Transparent <span className="text-primary-600">Pricing</span>
                            </h2>
                            <p className="mt-4 text-lg text-gray-500 font-medium max-w-xl mx-auto">
                                Start with our 30-day free trial on any plan. Cancel or switch plans anytime.
                            </p>
                        </div>

                        {/* Tab Switcher */}
                        <div className="flex justify-center mb-16">
                            <div className="bg-gray-100 p-1.5 rounded-2xl flex space-x-2 shadow-inner">
                                <button
                                    onClick={() => setPricingTab('restaurant')}
                                    className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${pricingTab === 'restaurant' ? 'bg-primary-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-800'}`}
                                >
                                    Restaurant SaaS
                                </button>
                                <button
                                    onClick={() => setPricingTab('builder')}
                                    className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${pricingTab === 'builder' ? 'bg-emerald-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-800'}`}
                                >
                                    Website Builder
                                </button>
                            </div>
                        </div>

                        {pricingTab === 'restaurant' ? (
                            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto animate-scale-in">
                                {[
                                    {
                                        name: 'Starter',
                                        desc: 'Ideal for starting your online presence.',
                                        price: '₹2,999',
                                        features: ['Restaurant Website', 'Customer Ordering System', 'Admin Dashboard', 'Online Payments Support', '500 orders/month', 'Technical Support']
                                    },
                                    {
                                        name: 'Professional',
                                        desc: 'Perfect for dedicated kitchen operations.',
                                        price: '₹6,999',
                                        features: ['Restaurant Website', 'Customer Ordering System', 'Admin Dashboard', 'Online Payments Support', 'Kitchen Display System (KDS)', 'Customer Mobile App', 'Admin Mobile App', 'Live Chatbot Integration', 'Unlimited orders', 'Technical Support'],
                                        popular: true
                                    },
                                    {
                                        name: 'Premium',
                                        desc: 'Complete restaurant management solution.',
                                        price: '₹9,999',
                                        features: ['Restaurant Website', 'Customer Ordering System', 'Admin Dashboard', 'Online Payments Support', 'Kitchen Display System (KDS)', 'Delivery Partner Dashboards', 'Customer Mobile App', 'Admin Mobile App', 'Kitchen & Delivery Mobile Apps', 'Live Chatbot Integration', 'WhatsApp Order Notifications', 'Unlimited orders', 'Priority Support']
                                    }
                                ].map((p) => (
                                    <div key={p.name} className={`bg-slate-50 rounded-3xl p-8 border-2 transition-all flex flex-col justify-between hover:shadow-xl relative ${p.popular ? 'border-primary-500 bg-white shadow-lg' : 'border-slate-100'}`}>
                                        {p.popular && (
                                            <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-primary-500 text-white font-black text-[10px] uppercase px-4 py-1.5 rounded-full tracking-widest">Most Popular</span>
                                        )}
                                        <div>
                                            <h3 className="text-2xl font-black text-gray-900">{p.name}</h3>
                                            <p className="text-gray-400 text-xs mt-1 leading-relaxed">{p.desc}</p>
                                            <div className="my-8 flex items-baseline">
                                                <span className="text-4xl font-black text-gray-900">{p.price}</span>
                                                <span className="text-gray-400 font-bold text-xs ml-1">/ month</span>
                                            </div>
                                            <ul className="space-y-4 mb-8">
                                                {p.features.map((f, i) => (
                                                    <li key={i} className="flex items-center text-xs text-gray-600 font-medium">
                                                        <CheckCircle2 className="w-4.5 h-4.5 text-primary-500 mr-2.5 flex-shrink-0" /> {f}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <button
                                            onClick={() => {
                                                const priceMap = { 'Starter': 2999, 'Professional': 6999, 'Premium': 9999 };
                                                setSelectedPlan({ name: p.name, billingCycle: 'monthly', price: priceMap[p.name] });
                                                setPreselectedPlan(true);
                                                setStep('register');
                                            }}
                                            className={`w-full py-4 text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-sm ${p.popular ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-md' : 'bg-white hover:bg-slate-100 text-gray-700 border border-slate-200'}`}
                                        >
                                            Start Free Trial
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto animate-scale-in">
                                {[
                                    {
                                        name: 'Free',
                                        desc: 'Start building your restaurant site.',
                                        price: '₹0',
                                        features: ['1 static website (Subdomain only)', 'Basic templates catalog', 'Platform domain (slug.aaharamcloud.com)', 'Aaharam branding footer', 'No custom domains']
                                    },
                                    {
                                        name: 'Basic',
                                        desc: 'Perfect for single food outlets.',
                                        price: '₹250',
                                        features: ['5 static websites', 'All templates catalog', 'Platform domain (slug.aaharamcloud.com)', '1 Custom Domain Publish', 'No platform branding', 'Standard email support']
                                    },
                                    {
                                        name: 'Pro',
                                        desc: 'For multi-outlet brands & aggregators.',
                                        price: '₹799',
                                        features: ['Unlimited static websites', 'Premium templates catalog', 'Unlimited Custom Domains', 'No platform branding', 'Advanced page visitor analytics', 'Priority support'],
                                        popular: true
                                    }
                                ].map((p) => (
                                    <div key={p.name} className={`bg-slate-50 rounded-3xl p-8 border-2 transition-all flex flex-col justify-between hover:shadow-xl relative ${p.popular ? 'border-emerald-500 bg-white shadow-lg' : 'border-slate-100'}`}>
                                        {p.popular && (
                                            <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-white font-black text-[10px] uppercase px-4 py-1.5 rounded-full tracking-widest">Most Popular</span>
                                        )}
                                        <div>
                                            <h3 className="text-2xl font-black text-gray-900">{p.name}</h3>
                                            <p className="text-gray-400 text-xs mt-1 leading-relaxed">{p.desc}</p>
                                            <div className="my-8 flex items-baseline">
                                                <span className="text-4xl font-black text-gray-900">{p.price}</span>
                                                <span className="text-gray-400 font-bold text-xs ml-1">/ month</span>
                                            </div>
                                            <ul className="space-y-4 mb-8">
                                                {p.features.map((f, i) => (
                                                    <li key={i} className="flex items-center text-xs text-gray-600 font-medium">
                                                        <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 mr-2.5 flex-shrink-0" /> {f}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <Link
                                            to="/sites/login"
                                            className={`w-full py-4 text-center text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-sm block ${p.popular ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md' : 'bg-white hover:bg-slate-100 text-gray-700 border border-slate-200'}`}
                                        >
                                            Start Free Trial
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Indian Business Compliant Footer */}
                <footer className="bg-slate-900 text-slate-400 py-8 border-t border-slate-800 text-left">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-3 gap-6 mb-6">
                            <div>
                                <div className="flex items-center space-x-3 mb-2">
                                    <img src="/aaharam_logo.jpg" alt="Aaharam Cloud" className="w-14 h-14 rounded-xl object-contain" />
                                    <span className="text-xl font-black text-white tracking-tighter">AAHARAM<span className="text-primary-500">CLOUD</span></span>
                                </div>
                                <p className="text-xs mt-2 text-slate-500 max-w-sm">
                                    Aaharam Cloud is India's leading multi-tenant SaaS platform for cloud kitchens, empowering local food merchants to launch their online delivery business commission-free.
                                </p>
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-2">Legal & Compliance</h4>
                                <div className="flex flex-col space-y-2 text-xs">
                                    <button type="button" onClick={() => setActivePolicy('privacy')} className="hover:text-white transition-colors text-left">Privacy Policy</button>
                                    <button type="button" onClick={() => setActivePolicy('terms')} className="hover:text-white transition-colors text-left">Terms of Service</button>
                                    <button type="button" onClick={() => setActivePolicy('refund')} className="hover:text-white transition-colors text-left">Refund & Cancellation Policy</button>
                                </div>
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-2">Corporate Office</h4>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                    Aaharam Cloud Tech Private Limited<br />
                                    100 Feet Road, T Nagar,<br />
                                    Chennai, Tamil Nadu - 600017<br />
                                    <span className="font-mono mt-1 block text-slate-400">GSTIN: 33AAICA8990F1Z4</span>
                                    <span className="block text-slate-400">Support: support@aaharamcloud.in</span>
                                </p>
                            </div>
                        </div>
                        <div className="pt-4 border-t border-slate-800 text-center text-xs text-slate-600 flex flex-col sm:flex-row justify-between items-center gap-4">
                            <p>© 2026 Aaharam Cloud Tech Pvt. Ltd. All rights reserved.</p>
                            <p className="italic">Made with ❤️ for food merchants in India</p>
                        </div>
                    </div>
                </footer>
                {activePolicy && renderPolicyModal()}
            </div>
        );
    }

    if (step === 'register') {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
                <Navbar />
                <div className="flex-1 flex items-center justify-center p-4 py-12">
                    <div className="max-w-3xl w-full bg-white rounded-[32px] shadow-2xl overflow-hidden border border-slate-100 transition-all duration-300">
                        {/* Premium Gradient Header Banner */}
                        <div className="bg-gradient-to-r from-primary-700 via-primary-600 to-primary-500 p-8 text-white text-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.2),transparent_60%)]"></div>
                            <Building2 className="w-10 h-10 mx-auto mb-3 text-white opacity-90 animate-pulse" />
                            <h2 className="text-3xl font-black tracking-tight">Join Aaharam Cloud</h2>
                            <p className="text-white opacity-85 text-xs mt-2 uppercase tracking-widest font-bold">Enter your basic details to get started.</p>
                        </div>

                        <form onSubmit={handleRegisterStepSubmit} className="p-6 md:p-10 space-y-8">
                            
                            {/* SECTION 1: BRAND PROFILE */}
                            <div className="space-y-4">
                                <div className="flex items-center space-x-2 border-b border-slate-100 pb-2">
                                    <ChefHat className="w-4 h-4 text-primary-600" />
                                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Brand Profile</h3>
                                </div>
                                <div className="grid md:grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Restaurant Name</label>
                                        <input 
                                            required 
                                            className="w-full bg-slate-50 border border-slate-100 focus:border-primary-500 focus:bg-white rounded-2xl p-4 focus:ring-2 focus:ring-primary-500/20 transition-all outline-none font-semibold text-slate-800 text-sm" 
                                            placeholder="e.g. Spice Route" 
                                            value={formData.name} 
                                            onChange={e => {
                                                const val = e.target.value;
                                                const cleanSlug = val.toLowerCase().replace(/[^a-z0-9]/g, '');
                                                setFormData(prev => ({
                                                    ...prev,
                                                    name: val,
                                                    slug: prev.slug === prev.name.toLowerCase().replace(/[^a-z0-9]/g, '') ? cleanSlug : prev.slug
                                                }));
                                            }} 
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Subdomain Link</label>
                                        <div className="relative flex items-center">
                                            <input 
                                                required 
                                                type="text" 
                                                className="w-full bg-slate-50 border border-slate-100 focus:border-primary-500 focus:bg-white rounded-2xl p-4 pr-36 focus:ring-2 focus:ring-primary-500/20 transition-all outline-none font-semibold text-slate-800 text-sm" 
                                                placeholder="e.g. spiceroute" 
                                                value={formData.slug} 
                                                onChange={e => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })} 
                                            />
                                            <span className="absolute right-4 text-xs font-bold text-slate-400 font-mono">
                                                .aaharamcloud.com
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-[10px] px-1">
                                            <p className="text-slate-400">
                                                Link: <span className="font-mono text-primary-600 font-bold">https://{formData.slug || 'your-slug'}.aaharamcloud.com</span>
                                            </p>
                                            {slugStatus === 'checking' && (
                                                <span className="text-slate-500 font-bold flex items-center gap-1">
                                                    <span className="w-2.5 h-2.5 border border-slate-450 border-t-transparent rounded-full animate-spin"></span>
                                                    Checking...
                                                </span>
                                            )}
                                            {slugStatus === 'available' && (
                                                <span className="text-green-600 font-bold">✓ Available</span>
                                            )}
                                            {slugStatus === 'taken' && (
                                                <span className="text-red-500 font-black">✗ Taken</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* SECTION 2: ADMINISTRATOR & CONTACT */}
                            <div className="space-y-4">
                                <div className="flex items-center space-x-2 border-b border-slate-100 pb-2">
                                    <Shield className="w-4 h-4 text-primary-600" />
                                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Account Credentials</h3>
                                </div>
                                <div className="grid md:grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Admin Email</label>
                                        <input 
                                            type="email" 
                                            required 
                                            className="w-full bg-slate-50 border border-slate-100 focus:border-primary-500 focus:bg-white rounded-2xl p-4 focus:ring-2 focus:ring-primary-500/20 transition-all outline-none font-semibold text-slate-800 text-sm" 
                                            placeholder="owner@gmail.com" 
                                            value={formData.email} 
                                            onChange={e => setFormData({ ...formData, email: e.target.value })} 
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Phone Number</label>
                                        <input 
                                            type="tel" 
                                            required 
                                            className="w-full bg-slate-50 border border-slate-100 focus:border-primary-500 focus:bg-white rounded-2xl p-4 focus:ring-2 focus:ring-primary-500/20 transition-all outline-none font-semibold text-slate-800 text-sm" 
                                            placeholder="+91 90000 00000" 
                                            value={formData.phone} 
                                            onChange={e => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })} 
                                        />
                                    </div>
                                    <div className="space-y-1.5 md:col-span-2">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Admin Password</label>
                                        <input 
                                            type="password" 
                                            required 
                                            className="w-full bg-slate-50 border border-slate-100 focus:border-primary-500 focus:bg-white rounded-2xl p-4 focus:ring-2 focus:ring-primary-500/20 transition-all outline-none font-semibold text-slate-800 text-sm" 
                                            placeholder="••••••••" 
                                            value={formData.password} 
                                            onChange={e => setFormData({ ...formData, password: e.target.value })} 
                                        />
                                        
                                        {/* Password Requirement Checklist */}
                                        {formData.password && (
                                            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100/70 space-y-2 mt-2">
                                                <p className="text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1">Security Requirements</p>
                                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                                    <div className={`text-[11px] flex items-center ${formData.password.length >= 8 ? 'text-green-600 font-semibold' : 'text-slate-400'}`}>
                                                        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${formData.password.length >= 8 ? 'bg-green-650' : 'bg-slate-300'}`} />
                                                        8+ Chars
                                                    </div>
                                                    <div className={`text-[11px] flex items-center ${/[A-Z]/.test(formData.password) ? 'text-green-600 font-semibold' : 'text-slate-400'}`}>
                                                        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${/[A-Z]/.test(formData.password) ? 'bg-green-650' : 'bg-slate-300'}`} />
                                                        Uppercase
                                                    </div>
                                                    <div className={`text-[11px] flex items-center ${/[a-z]/.test(formData.password) ? 'text-green-600 font-semibold' : 'text-slate-400'}`}>
                                                        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${/[a-z]/.test(formData.password) ? 'bg-green-650' : 'bg-slate-300'}`} />
                                                        Lowercase
                                                    </div>
                                                    <div className={`text-[11px] flex items-center ${/[0-9]/.test(formData.password) ? 'text-green-600 font-semibold' : 'text-slate-400'}`}>
                                                        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${/[0-9]/.test(formData.password) ? 'bg-green-650' : 'bg-slate-300'}`} />
                                                        Number
                                                    </div>
                                                    <div className={`text-[11px] flex items-center ${/[^A-Za-z0-9]/.test(formData.password) ? 'text-green-600 font-semibold' : 'text-slate-400'}`}>
                                                        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${/[^A-Za-z0-9]/.test(formData.password) ? 'bg-green-650' : 'bg-slate-300'}`} />
                                                        Special
                                                    </div>
                                                </div>
                                                
                                                {/* Strength Bar */}
                                                <div className="mt-3 h-1 w-full bg-slate-200 rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full transition-all duration-300 ${
                                                            [
                                                                formData.password.length >= 8,
                                                                /[A-Z]/.test(formData.password),
                                                                /[a-z]/.test(formData.password),
                                                                /[0-9]/.test(formData.password),
                                                                /[^A-Za-z0-9]/.test(formData.password)
                                                            ].filter(Boolean).length <= 2 ? 'bg-red-500 w-1/3' : 
                                                            [
                                                                formData.password.length >= 8,
                                                                /[A-Z]/.test(formData.password),
                                                                /[a-z]/.test(formData.password),
                                                                /[0-9]/.test(formData.password),
                                                                /[^A-Za-z0-9]/.test(formData.password)
                                                            ].filter(Boolean).length <= 4 ? 'bg-yellow-500 w-2/3' : 'bg-green-550 w-full'
                                                        }`}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* SECTION 3: STORE LOCATION */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                    <div className="flex items-center space-x-2">
                                        <Globe2 className="w-4 h-4 text-primary-600" />
                                        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Main Address</h3>
                                    </div>
                                    <div className="flex bg-slate-100 rounded-lg p-0.5 text-xs font-semibold">
                                        <button
                                            type="button"
                                            onClick={() => setAddressMode('map')}
                                            className={`px-3 py-1 rounded-md transition-all ${addressMode === 'map' ? 'bg-white text-slate-800 shadow-sm font-bold' : 'text-slate-500 hover:text-slate-850'}`}
                                        >
                                            📍 Maps
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setAddressMode('manual')}
                                            className={`px-3 py-1 rounded-md transition-all ${addressMode === 'manual' ? 'bg-white text-slate-800 shadow-sm font-bold' : 'text-slate-500 hover:text-slate-850'}`}
                                        >
                                            ✍️ Manual
                                        </button>
                                    </div>
                                </div>

                                {addressMode === 'map' ? (
                                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                                        <LocationSearch
                                            onLocationSelect={(loc) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    address: loc.address,
                                                    latitude: loc.lat,
                                                    longitude: loc.lng
                                                }));
                                            }}
                                            selectedLocation={formData.address ? {
                                                address: formData.address,
                                                lat: formData.latitude,
                                                lng: formData.longitude
                                            } : null}
                                        />
                                    </div>
                                ) : (
                                    <textarea
                                        rows="2"
                                        required
                                        className="w-full bg-slate-50 border border-slate-100 focus:border-primary-500 focus:bg-white rounded-2xl p-4 focus:ring-2 focus:ring-primary-500/20 transition-all outline-none font-semibold text-slate-800 text-sm"
                                        placeholder="Enter complete restaurant address (Street details, City, Pin Code)"
                                        value={formData.address}
                                        onChange={e => setFormData({ ...formData, address: e.target.value })}
                                    />
                                )}
                            </div>

                            {/* Submit Button */}
                            {(() => {
                                const isFormInvalid = 
                                    !formData.name?.trim() || 
                                    !formData.email?.trim() || 
                                    !formData.password?.trim() || 
                                    !formData.phone?.trim() || 
                                    !formData.slug?.trim() || 
                                    !formData.address?.trim() || 
                                    slugStatus === 'taken' || 
                                    slugStatus === 'checking';

                                return (
                                    <button 
                                        type="submit" 
                                        disabled={isFormInvalid} 
                                        className="w-full btn-primary py-5 text-base font-black uppercase tracking-widest shadow-2xl shadow-primary-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none rounded-2xl"
                                    >
                                        {slugStatus === 'checking' ? (
                                            <>
                                                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Verifying Link...</span>
                                            </>
                                        ) : (
                                            preselectedPlan ? 'Continue to Checkout' : 'Select Plan & Continue'
                                        )}
                                    </button>
                                );
                            })()}
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'plans') {
        const toggleCycle = () => {
            const newCycle = selectedPlan.billingCycle === 'monthly' ? 'yearly' : 'monthly';
            const priceMap = {
                Starter: { monthly: 2999, yearly: 2399 },
                Professional: { monthly: 6999, yearly: 5599 },
                Premium: { monthly: 9999, yearly: 7999 }
            };
            setSelectedPlan({
                name: selectedPlan.name,
                billingCycle: newCycle,
                price: priceMap[selectedPlan.name][newCycle]
            });
        };

        const plans = [
            {
                name: 'Starter',
                desc: 'Ideal for starting your online presence.',
                monthlyPrice: 2999,
                yearlyPrice: 2399,
                features: ['Restaurant Website', 'Customer Ordering System', 'Admin Dashboard', 'Online Payments Support', '500 orders/month', 'Technical Support']
            },
            {
                name: 'Professional',
                desc: 'Perfect for dedicated kitchen operations.',
                monthlyPrice: 6999,
                yearlyPrice: 5599,
                features: ['Restaurant Website', 'Customer Ordering System', 'Admin Dashboard', 'Online Payments Support', 'Kitchen Display System (KDS)', 'Customer Mobile App', 'Admin Mobile App', 'Live Chatbot Integration', 'Unlimited orders', 'Technical Support'],
                popular: true
            },
            {
                name: 'Premium',
                desc: 'Complete restaurant management solution.',
                monthlyPrice: 9999,
                yearlyPrice: 7999,
                features: ['Restaurant Website', 'Customer Ordering System', 'Admin Dashboard', 'Online Payments Support', 'Kitchen Display System (KDS)', 'Delivery Partner Dashboards', 'Customer Mobile App', 'Admin Mobile App', 'Kitchen & Delivery Mobile Apps', 'Live Chatbot Integration', 'WhatsApp Order Notifications', 'Unlimited orders', 'Priority Support']
            }
        ];

        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Navbar />
                <div className="max-w-6xl mx-auto py-16 px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-5xl font-black mb-4">Choose Your SaaS Plan</h2>
                        <p className="text-gray-400 font-medium">All plans start with a 30-day free trial. Cancel anytime.</p>

                        {/* Billing Cycle Toggle */}
                        <div className="mt-8 inline-flex items-center space-x-4 bg-gray-200 p-1.5 rounded-2xl">
                            <button onClick={toggleCycle} className={`px-6 py-2 rounded-xl text-xs font-black uppercase transition-all ${selectedPlan.billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500'}`}>Monthly</button>
                            <button onClick={toggleCycle} className={`px-6 py-2 rounded-xl text-xs font-black uppercase transition-all ${selectedPlan.billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500'}`}>Yearly (Save 20%)</button>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {plans.map((p) => {
                            const price = selectedPlan.billingCycle === 'monthly' ? p.monthlyPrice : p.yearlyPrice;
                            return (
                                <div key={p.name} className={`bg-white rounded-3xl p-8 border-4 transition-all flex flex-col justify-between ${p.popular ? 'border-primary-500 shadow-2xl relative' : 'border-gray-200'}`}>
                                    {p.popular && (
                                        <span className="absolute -top-5 left-1/2 -translate-x-1/2 bg-primary-500 text-white font-black text-xs uppercase px-4 py-1.5 rounded-full tracking-widest">Most Popular</span>
                                    )}
                                    <div>
                                        <h3 className="text-2xl font-black text-gray-900">{p.name}</h3>
                                        <p className="text-gray-400 text-sm mt-1">{p.desc}</p>
                                        <div className="my-8 flex items-baseline">
                                            <span className="text-5xl font-black text-gray-900">₹{price.toLocaleString()}</span>
                                            <span className="text-gray-400 font-bold text-sm ml-2">/ month</span>
                                        </div>
                                        <ul className="space-y-4 mb-8">
                                            {p.features.map((f, i) => (
                                                <li key={i} className="flex items-center text-sm text-gray-600 font-medium">
                                                    <CheckCircle2 className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0" /> {f}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setSelectedPlan({ name: p.name, billingCycle: selectedPlan.billingCycle, price });
                                            setStep('checkout');
                                        }}
                                        className={`w-full py-4 text-sm font-black uppercase tracking-widest rounded-xl shadow-md transition-all ${p.popular ? 'bg-primary-600 hover:bg-primary-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                                    >
                                        Try 30 Days Free
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <div className="text-center mt-8">
                        <button onClick={() => setStep('register')} className="text-sm font-bold text-gray-400 hover:text-gray-700">← Back to Registration Details</button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'checkout') {
        const trialEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(undefined, {
            year: 'numeric', month: 'long', day: 'numeric'
        });

        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center p-4 py-12">
                    <div className="max-w-xl w-full bg-white rounded-3xl shadow-3xl overflow-hidden border border-gray-100">
                        <div className="bg-primary-600 p-8 text-white text-center">
                            <h2 className="text-3xl font-black">Confirm Registration</h2>
                            <p className="opacity-80 mt-2">Claim your 30-day free trial on Razorpay</p>
                        </div>
                        <div className="p-8 space-y-8">

                            {/* Summary Card */}
                            <div className="bg-gradient-to-tr from-gray-900 to-slate-800 rounded-2xl p-6 text-white shadow-xl space-y-4">
                                <div className="flex justify-between items-center border-b border-gray-700/60 pb-4">
                                    <div>
                                        <p className="text-[10px] uppercase font-black tracking-widest text-primary-400">Restaurant</p>
                                        <h3 className="text-xl font-black">{formData.name || 'Your Restaurant'}</h3>
                                    </div>
                                    <span className="bg-primary-500/20 text-primary-300 border border-primary-500/30 px-3 py-1 rounded-full text-xs font-bold">
                                        {formData.slug || 'slug'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-400 font-medium">Account Admin:</span>
                                    <span className="font-bold text-white">{formData.email}</span>
                                </div>
                            </div>

                            {/* Plan Summary */}
                            <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 flex justify-between items-center">
                                <div>
                                    <p className="text-xs uppercase font-black text-gray-400 tracking-wider">Plan Selected</p>
                                    <h4 className="text-lg font-black text-gray-900">{selectedPlan.name} Plan ({selectedPlan.billingCycle})</h4>
                                </div>
                                <div className="text-right">
                                    <p className="text-xl font-black text-primary-600">₹{selectedPlan.price.toLocaleString()}/mo</p>
                                    <p className="text-[10px] text-gray-400 italic">First debit on {trialEndDate}</p>
                                </div>
                            </div>

                            {/* Trial Information Alert */}
                            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 text-xs text-indigo-700 font-bold space-y-2 shadow-sm">
                                <p className="flex items-center text-sm font-black"><Sparkles className="w-4 h-4 mr-2 text-indigo-600" /> 30-Day Free Trial Included</p>
                                <p className="leading-relaxed">Clicking below will register your website and open the secure Razorpay payment gateway window to authorize your subscription mandate. No charge will be made during your trial period.</p>
                            </div>

                            <form onSubmit={handleRegisterSubscription} className="space-y-4">
                                <button type="submit" disabled={loading} className="w-full btn-primary py-5 text-md font-black uppercase tracking-widest shadow-2xl shadow-primary-200">
                                    {loading ? 'Processing...' : 'Authorize Trial'}
                                </button>
                                <p className="text-[10px] text-gray-400 font-medium text-center italic mt-2">Secured subscription mandate powered by Razorpay / Cashfree</p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'choice') {
        return (
            <>
                <div className="min-h-screen bg-white py-20 px-4">
                    <div className="max-w-5xl mx-auto font-black text-center">
                        <p className="text-primary-600 uppercase tracking-[0.2em] mb-4">Success! Account Created</p>
                        <h2 className="text-5xl lg:text-7xl mb-12">How do you want to <span className="text-gray-400">Proceed?</span></h2>

                        <div className="grid md:grid-cols-2 gap-8">
                            {/* Choice 1: Template */}
                            <div className="group card p-12 border-2 border-gray-100 hover:border-primary-500 cursor-pointer transition-all flex flex-col items-center" onClick={() => setStep('template-config')}>
                                <div className="w-24 h-24 bg-primary-100 text-primary-600 rounded-3xl flex items-center justify-center mb-8 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                                    <Layout className="w-12 h-12" />
                                </div>
                                <h3 className="text-3xl mb-4">Aaharam Default</h3>
                                <p className="text-gray-400 font-medium mb-8 leading-relaxed">
                                    Use our high-performance standard template. Fast, mobile-responsive, and optimized for sales. Zero setup fee.
                                </p>
                                <span className="text-primary-600 flex items-center">
                                    Start with template <ArrowRight className="ml-2 w-5 h-5" />
                                </span>
                            </div>

                            {/* Choice 2: Custom */}
                            <div className="group card p-12 bg-gray-900 text-white border-2 border-gray-900 hover:border-primary-500 cursor-pointer transition-all flex flex-col items-center" onClick={() => setStep('custom-design')}>
                                <div className="w-24 h-24 bg-gray-800 text-primary-500 rounded-3xl flex items-center justify-center mb-8 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                                    <Palette className="w-12 h-12" />
                                </div>
                                <h3 className="text-3xl mb-4 text-white">Custom Website</h3>
                                <p className="text-gray-400 font-medium mb-8 leading-relaxed">
                                    Get a dedicated designer for your brand. One-time website design plan with unique layouts and assets.
                                </p>
                                <span className="text-primary-500 flex items-center">
                                    Browse design plans <ArrowRight className="ml-2 w-5 h-5" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                {showTestCredentials && renderTestCredentialsModal()}
            </>
        );
    }

    if (step === 'template-config') {
        return (
            <div className="min-h-screen bg-gray-50 py-12 px-4">
                <div className="max-w-2xl mx-auto font-black">
                    <h2 className="text-4xl mb-8 flex items-center italic">
                        <Layout className="mr-4 text-primary-600" /> Style Your Website
                    </h2>
                    <div className="card p-10">
                        <form onSubmit={handleTemplateSubmit} className="space-y-8">
                            <div className="grid grid-cols-2 gap-8">
                                <div className="space-y-3">
                                    <label className="text-sm uppercase text-gray-400">Primary Color</label>
                                    <div className="flex items-center space-x-4">
                                        <input type="color" className="p-0 border-none w-16 h-16 rounded-xl cursor-pointer" value={configData.primaryColor} onChange={e => setConfigData({ ...configData, primaryColor: e.target.value })} />
                                        <span className="font-mono text-sm uppercase">{configData.primaryColor}</span>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <label className="text-sm uppercase text-gray-400">Secondary Color</label>
                                    <div className="flex items-center space-x-4">
                                        <input type="color" className="p-0 border-none w-16 h-16 rounded-xl cursor-pointer" value={configData.secondaryColor} onChange={e => setConfigData({ ...configData, secondaryColor: e.target.value })} />
                                        <span className="font-mono text-sm uppercase">{configData.secondaryColor}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <label className="text-sm uppercase text-gray-400">Logo Image</label>
                                <div className="flex items-center space-x-4">
                                    {configData.logoUrl ? (
                                        <div className="relative w-20 h-20 bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                                            <img src={configData.logoUrl} alt="Logo preview" className="w-full h-full object-contain" />
                                            <button type="button" onClick={() => setConfigData({ ...configData, logoUrl: '' })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors w-5 h-5 flex items-center justify-center text-[10px] font-bold">✕</button>
                                        </div>
                                    ) : (
                                        <label className="w-20 h-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 hover:border-primary-500 flex flex-col items-center justify-center cursor-pointer transition-colors flex-shrink-0">
                                            <ImageIcon className="text-gray-400 w-6 h-6" />
                                            <span className="text-[10px] text-gray-400 mt-1">Upload</span>
                                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                                        </label>
                                    )}
                                    <div className="flex-1 text-xs text-gray-400">
                                        {uploading ? (
                                            <span className="text-primary-600 font-semibold animate-pulse">Uploading to Cloudinary...</span>
                                        ) : (
                                            <span>Upload your restaurant logo (JPEG, PNG, max size 2 MB)</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button type="submit" disabled={loading} className="btn-primary w-full py-5 text-xl">
                                {loading ? 'Updating Design...' : 'Apply Theme & Go Live'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'custom-design') {
        const planFeatureMap = {
            Starter: [
                { key: 'restaurant_website', label: 'Custom Restaurant Website', included: true },
                { key: 'ordering_system', label: 'Customer Ordering System', included: true },
                { key: 'admin_dashboard', label: 'Admin Dashboard', included: true },
                { key: 'online_payments', label: 'Online Payments (Cashfree)', included: true },
                { key: 'technical_support', label: 'Technical Support', included: true },
            ],
            Professional: [
                { key: 'restaurant_website', label: 'Custom Restaurant Website', included: true },
                { key: 'ordering_system', label: 'Customer Ordering System', included: true },
                { key: 'admin_dashboard', label: 'Admin Dashboard', included: true },
                { key: 'kitchen_dashboard', label: 'Kitchen Dashboard', included: true },
                { key: 'customer_app', label: 'Customer Mobile App', included: true },
                { key: 'admin_app', label: 'Admin Mobile App', included: true },
                { key: 'technical_support', label: 'Technical Support', included: true },
            ],
            Premium: [
                { key: 'restaurant_website', label: 'Custom Restaurant Website', included: true },
                { key: 'ordering_system', label: 'Customer Ordering System', included: true },
                { key: 'admin_dashboard', label: 'Admin Dashboard', included: true },
                { key: 'kitchen_dashboard', label: 'Kitchen Dashboard', included: true },
                { key: 'delivery_dashboard', label: 'Delivery Dashboard', included: true },
                { key: 'customer_app', label: 'Customer Mobile App', included: true },
                { key: 'admin_app', label: 'Admin Mobile App', included: true },
                { key: 'kitchen_app', label: 'Kitchen Mobile App', included: true },
                { key: 'delivery_app', label: 'Delivery Mobile App', included: true },
                { key: 'whatsapp_notifications', label: 'WhatsApp Order Notifications', included: true },
                { key: 'priority_support', label: 'Priority Support', included: true },
            ]
        };

        const currentPlanFeatures = planFeatureMap[selectedPlan.name] || planFeatureMap.Starter;

        return (
            <div className="min-h-screen bg-gray-50 py-16 px-4">
                <div className="max-w-6xl mx-auto font-black">
                    <div className="text-center mb-12">
                        <p className="text-primary-600 uppercase tracking-[0.2em] text-sm mb-3">Custom Website Request</p>
                        <h2 className="text-5xl mb-4">Build Your <span className="text-primary-600">Dream Website</span></h2>
                        <p className="text-gray-400 font-medium max-w-2xl mx-auto">Our design team will build a custom website tailored to your brand. Once deployed, we'll map it to your Aaharam Cloud backend so everything works seamlessly on your subdomain.</p>
                    </div>

                    <div className="grid lg:grid-cols-5 gap-8">
                        {/* Left: Plan Summary & Features */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Selected Plan Card */}
                            <div className="card bg-gray-900 text-white p-8">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Your SaaS Plan</p>
                                <h3 className="text-3xl mb-1">{selectedPlan.name}</h3>
                                <p className="text-primary-500 text-lg">₹{selectedPlan.price.toLocaleString()}<span className="text-gray-500 text-sm"> /mo</span></p>

                                <div className="mt-6 pt-6 border-t border-gray-800 space-y-3">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Included Features</p>
                                    {currentPlanFeatures.map((f) => (
                                        <div key={f.key} className="flex items-center text-sm">
                                            <CheckCircle2 className="w-4 h-4 text-primary-500 mr-3 flex-shrink-0" />
                                            <span className="text-gray-300">{f.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* How It Works */}
                            <div className="card p-8 border-2 border-gray-100">
                                <h4 className="text-lg mb-6 flex items-center"><Rocket className="w-5 h-5 text-primary-600 mr-3" /> How It Works</h4>
                                <div className="space-y-4">
                                    {[
                                        { step: '01', title: 'Submit Request', desc: 'Share your brand details & preferences' },
                                        { step: '02', title: 'Design & Build', desc: 'Our team creates your custom website' },
                                        { step: '03', title: 'Deploy & Map', desc: 'Website deployed on Vercel/Netlify' },
                                        { step: '04', title: 'Go Live', desc: 'Mapped to your Aaharam Cloud subdomain' }
                                    ].map((s) => (
                                        <div key={s.step} className="flex items-start space-x-4">
                                            <span className="text-primary-600 text-2xl font-mono">{s.step}</span>
                                            <div>
                                                <p className="text-gray-900 text-sm">{s.title}</p>
                                                <p className="text-gray-400 text-xs font-medium">{s.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right: Design Request Form */}
                        <div className="lg:col-span-3">
                            <div className="card p-10 border-2 border-gray-100">
                                <h3 className="text-2xl mb-2 flex items-center">
                                    <Palette className="mr-3 text-primary-600 w-7 h-7" /> Branding & Design Details
                                </h3>
                                <p className="text-gray-400 text-sm font-medium mb-8">Fill in the details so our team can create a website that matches your restaurant's identity.</p>

                                <form onSubmit={handleCustomDesignSubmit} className="space-y-6">
                                    {/* Business Description */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] uppercase text-gray-400 font-black tracking-widest">Describe Your Restaurant</label>
                                        <textarea
                                            className="w-full bg-gray-50 border-none rounded-2xl p-4 focus:ring-2 focus:ring-primary-500"
                                            rows="3"
                                            placeholder="e.g. A modern South Indian restaurant specializing in authentic Chettinad cuisine with a contemporary twist..."
                                            value={configData.notes}
                                            onChange={e => setConfigData({ ...configData, notes: e.target.value })}
                                        />
                                    </div>

                                    {/* Color Preferences */}
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-3">
                                            <label className="text-[10px] uppercase text-gray-400 font-black tracking-widest">Primary Brand Color</label>
                                            <div className="flex items-center space-x-4 bg-gray-50 rounded-2xl p-3">
                                                <input type="color" className="p-0 border-none w-12 h-12 rounded-xl cursor-pointer" value={configData.primaryColor} onChange={e => setConfigData({ ...configData, primaryColor: e.target.value })} />
                                                <span className="font-mono text-sm uppercase text-gray-500">{configData.primaryColor}</span>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <label className="text-[10px] uppercase text-gray-400 font-black tracking-widest">Secondary Color</label>
                                            <div className="flex items-center space-x-4 bg-gray-50 rounded-2xl p-3">
                                                <input type="color" className="p-0 border-none w-12 h-12 rounded-xl cursor-pointer" value={configData.secondaryColor} onChange={e => setConfigData({ ...configData, secondaryColor: e.target.value })} />
                                                <span className="font-mono text-sm uppercase text-gray-500">{configData.secondaryColor}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Logo Upload */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] uppercase text-gray-400 font-black tracking-widest">Logo / Moodboard Image</label>
                                        <div className="flex items-center space-x-4 bg-gray-50 rounded-2xl p-4">
                                            {configData.logoUrl ? (
                                                <div className="relative w-16 h-16 bg-white rounded-xl border overflow-hidden flex items-center justify-center flex-shrink-0">
                                                    <img src={configData.logoUrl} alt="Logo preview" className="w-full h-full object-contain" />
                                                    <button type="button" onClick={() => setConfigData({ ...configData, logoUrl: '' })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors w-4 h-4 flex items-center justify-center text-[8px] font-bold">✕</button>
                                                </div>
                                            ) : (
                                                <label className="w-16 h-16 bg-white rounded-xl border-2 border-dashed hover:border-primary-500 flex flex-col items-center justify-center cursor-pointer transition-colors flex-shrink-0">
                                                    <ImageIcon className="text-gray-400 w-5 h-5" />
                                                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                                                </label>
                                            )}
                                            <div className="flex-1 text-[11px] text-gray-400">
                                                {uploading ? (
                                                    <span className="text-primary-600 font-semibold animate-pulse">Uploading to Cloudinary...</span>
                                                ) : (
                                                    <span>Upload your brand logo or design reference (max size 2 MB)</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reference URLs */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] uppercase text-gray-400 font-black tracking-widest">Reference Websites (Optional)</label>
                                        <input
                                            className="w-full bg-gray-50 border-none rounded-2xl p-4 focus:ring-2 focus:ring-primary-500"
                                            placeholder="e.g. https://example-restaurant.com, https://another-inspiration.com"
                                            value={configData.referenceUrls || ''}
                                            onChange={e => setConfigData({ ...configData, referenceUrls: e.target.value })}
                                        />
                                        <p className="text-[10px] text-gray-400 font-medium">Share websites you like for design inspiration</p>
                                    </div>

                                    {/* Special Requirements */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] uppercase text-gray-400 font-black tracking-widest">Special Requirements (Optional)</label>
                                        <textarea
                                            className="w-full bg-gray-50 border-none rounded-2xl p-4 focus:ring-2 focus:ring-primary-500"
                                            rows="2"
                                            placeholder="e.g. I want a dark theme, animated hero section, gallery of dishes..."
                                            value={configData.specialNotes || ''}
                                            onChange={e => setConfigData({ ...configData, specialNotes: e.target.value })}
                                        />
                                    </div>

                                    {/* Info Banner */}
                                    <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 text-xs text-indigo-700 font-bold space-y-1.5">
                                        <p className="flex items-center text-sm font-black"><Globe className="w-4 h-4 mr-2" /> Custom Website Deployment</p>
                                        <p>Once your website is built, our team will deploy it and map it to your Aaharam Cloud backend. Your restaurant will be accessible at <span className="font-mono underline">{formData.name ? formData.name.toLowerCase().replace(/\s+/g, '-') : 'your-restaurant'}.aaharamcloud.com</span></p>
                                    </div>

                                    <button type="submit" disabled={loading} className="w-full btn-primary py-5 text-md font-black uppercase tracking-widest shadow-2xl shadow-primary-200 flex items-center justify-center">
                                        {loading ? 'Submitting...' : 'Submit Design Request'}
                                        {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="text-center mt-8">
                        <button onClick={() => setStep('choice')} className="text-sm font-bold text-gray-400 hover:text-gray-700">← Back to Options</button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center p-4">
                <div className="max-w-md w-full text-center space-y-8 font-black">
                    <div className="w-32 h-32 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto animate-bounce">
                        <CheckCircle2 className="w-16 h-16" />
                    </div>
                    <h1 className="text-4xl lg:text-6xl text-gray-900 italic tracking-tighter">Your Store Is <span className="text-primary-600 underline">LIVE!</span></h1>

                    <div className="bg-gray-50 p-8 rounded-3xl border-2 border-gray-100 text-left">
                        <p className="text-[10px] text-gray-400 uppercase tracking-[0.3em] mb-3">Live Production URL</p>
                        <a href={regResponse.subdomainUrl} target="_blank" rel="noopener noreferrer" className="text-2xl font-mono text-primary-600 underline break-all">
                            {regResponse.subdomainUrl}
                        </a>
                        <p className="text-xs text-gray-500 mt-6 font-medium italic">* Redirecting to your dashboard shortly...</p>
                    </div>

                    <button
                        onClick={() => window.location.href = regResponse.subdomainUrl}
                        className="btn-primary w-full py-6 text-xl shadow-2xl shadow-primary-200 flex items-center justify-center group"
                    >
                        Go to My Dashboard <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform" />
                    </button>
                    <button onClick={() => setStep('landing')} className="text-gray-400 hover:text-gray-900 transition-colors uppercase tracking-widest text-xs font-black">Back to Home</button>
                </div>
            </div>
        );
    }

    if (step === 'success-custom') {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4 text-white">
                <div className="max-w-2xl w-full text-center space-y-8 font-black">
                    <div className="w-32 h-32 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto shadow-4xl shadow-primary-500/50">
                        <MousePointer2 className="w-16 h-16" />
                    </div>
                    <div>
                        <h2 className="text-5xl lg:text-7xl mb-4 italic italic">Request <span className="text-primary-500">Received!</span></h2>
                        <p className="text-gray-400 text-xl font-medium">Our expert tech team will contact you shortly to refine the design for <span className="text-white underline">{formData.name}</span>.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div onClick={() => window.open(regResponse.subdomainUrl)} className="bg-gray-800 p-8 rounded-3xl border-2 border-transparent hover:border-primary-500 cursor-pointer transition-all">
                            <Globe className="text-primary-500 w-10 h-10 mb-4 mx-auto" />
                            <h4 className="text-xl">View Staging</h4>
                            <p className="text-gray-500 text-sm mt-2">See your site with the default template while we design.</p>
                        </div>
                        <div onClick={() => setStep('landing')} className="bg-gray-800 p-8 rounded-3xl border-2 border-transparent hover:border-gray-500 cursor-pointer transition-all">
                            <Building2 className="text-gray-500 w-10 h-10 mb-4 mx-auto" />
                            <h4 className="text-xl">Platform Home</h4>
                            <p className="text-gray-500 text-sm mt-2">Return to Aaharam Cloud main portal.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
